import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { ArrowRightLeft, DollarSign, X, ArrowDownToLine, ArrowUpFromLine, Users, AlertTriangle, RefreshCw } from 'lucide-react';
import { PartnerDropdown } from '../components/PartnerDropdown';
import { PartnerDaybookModal, PartnerSummaryModal } from '../components/PartnerDaybook';

const CashPool = () => {
  const [balance, setBalance] = useState(null);
  const [partnerBalances, setPartnerBalances] = useState([]);
  const [conversions, setConversions] = useState([]);
  const [usdtToInrConversions, setUsdtToInrConversions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConversionForm, setShowConversionForm] = useState(false);
  const [showUsdtToInrForm, setShowUsdtToInrForm] = useState(false);
  const [showPettyCashForm, setShowPettyCashForm] = useState(false);
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [activeTab, setActiveTab] = useState('inrToUsdt');
  const [daybookPartner, setDaybookPartner] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);
  const [recomputing, setRecomputing] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  const [transferData, setTransferData] = useState({
    source_partner_id: '',
    destination_partner_id: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  
  const [conversionFormData, setConversionFormData] = useState({
    amount_inr: '',
    conversion_rate: '85',
    destination_exchange: '',
    partner_id: '',
    fee: '0',
    notes: ''
  });

  const [usdtToInrFormData, setUsdtToInrFormData] = useState({
    usdt_amount: '',
    conversion_rate: '85',
    source_exchange: '',
    partner_id: '',
    fee_usdt: '0',
    notes: ''
  });

  const [pettyCashAmount, setPettyCashAmount] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [balanceRes, conversionsRes, usdtToInrRes, partnerBalRes] = await Promise.all([
        axiosInstance.get('/cash-pool/balance'),
        axiosInstance.get('/cash-pool/conversions'),
        axiosInstance.get('/cash-pool/usdt-to-inr'),
        axiosInstance.get('/cash-pool/partner-balances').catch(() => ({ data: [] })),
      ]);
      setBalance(balanceRes.data);
      setConversions(conversionsRes.data);
      setUsdtToInrConversions(usdtToInrRes.data);
      setPartnerBalances(partnerBalRes.data || []);
      setPettyCashAmount(balanceRes.data.petty_cash.toString());
      // Drift detection (admin-only)
      if (user.is_admin) {
        axiosInstance.get('/cash-pool/diagnose')
          .then((d) => setDiagnostic(d.data))
          .catch(() => setDiagnostic(null));
      }
    } catch (error) {
      toast.error('Failed to fetch cash pool data');
    } finally {
      setLoading(false);
    }
  };

  const handleRecompute = async () => {
    if (!window.confirm(
      'Recompute Cash Pool from approved ledger entries?\n\n' +
      'This will overwrite the stored balance with the source-of-truth value computed from ' +
      'approved deposits, withdrawals, expenses, payouts, and conversions. Petty cash is preserved.'
    )) return;
    setRecomputing(true);
    try {
      const r = await axiosInstance.post('/cash-pool/recompute');
      toast.success(
        `Cash pool recomputed. Drift corrected: ₹${(r.data.drift_corrected || 0).toLocaleString('en-IN')}`
      );
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recompute failed');
    } finally {
      setRecomputing(false);
    }
  };

  const handleConversion = async (e) => {
    e.preventDefault();
    if (!conversionFormData.partner_id) {
      toast.error("Select the partner whose INR cash pool is being drained");
      return;
    }
    try {
      await axiosInstance.post('/cash-pool/convert', {
        ...conversionFormData,
        amount_inr: parseFloat(conversionFormData.amount_inr),
        conversion_rate: parseFloat(conversionFormData.conversion_rate),
        fee: parseFloat(conversionFormData.fee)
      });
      toast.success('Cash converted to USDT successfully');
      fetchData();
      closeConversionForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert cash');
    }
  };

  const handleUsdtToInrConversion = async (e) => {
    e.preventDefault();
    if (!usdtToInrFormData.partner_id) {
      toast.error("Select the partner whose INR cash pool receives the INR");
      return;
    }
    try {
      await axiosInstance.post('/cash-pool/usdt-to-inr', {
        usdt_amount: parseFloat(usdtToInrFormData.usdt_amount),
        conversion_rate: parseFloat(usdtToInrFormData.conversion_rate),
        source_exchange: usdtToInrFormData.source_exchange,
        partner_id: usdtToInrFormData.partner_id,
        fee_usdt: parseFloat(usdtToInrFormData.fee_usdt),
        notes: usdtToInrFormData.notes
      });
      toast.success('USDT converted to INR - Cash Pool funded');
      fetchData();
      closeUsdtToInrForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert USDT');
    }
  };

  const handlePettyCashUpdate = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.put('/cash-pool/petty-cash', {
        amount: parseFloat(pettyCashAmount)
      });
      toast.success('Petty cash updated');
      fetchData();
      setShowPettyCashForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update petty cash');
    }
  };

  const handlePartnerTransfer = async (e) => {
    e.preventDefault();
    if (!transferData.source_partner_id || !transferData.destination_partner_id) {
      toast.error('Select both source and destination partners');
      return;
    }
    if (transferData.source_partner_id === transferData.destination_partner_id) {
      toast.error('Source and destination must differ');
      return;
    }
    const amt = parseFloat(transferData.amount);
    if (!amt || amt <= 0) {
      toast.error('Enter a positive amount');
      return;
    }
    try {
      await axiosInstance.post('/cash-pool/partner-transfer', {
        source_partner_id: transferData.source_partner_id,
        destination_partner_id: transferData.destination_partner_id,
        amount: amt,
        date: transferData.date,
        notes: transferData.notes,
      });
      toast.success('Partner transfer recorded');
      setShowTransferForm(false);
      setTransferData({
        source_partner_id: '',
        destination_partner_id: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        notes: '',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record transfer');
    }
  };


  const closeConversionForm = () => {
    setShowConversionForm(false);
    setConversionFormData({
      amount_inr: '',
      conversion_rate: '85',
      destination_exchange: '',
      partner_id: '',
      fee: '0',
      notes: ''
    });
  };

  const closeUsdtToInrForm = () => {
    setShowUsdtToInrForm(false);
    setUsdtToInrFormData({
      usdt_amount: '',
      conversion_rate: '85',
      source_exchange: '',
      partner_id: '',
      fee_usdt: '0',
      notes: ''
    });
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="cash-pool-page">
      <div className="cb-page-header">
        <p className="cb-eyebrow">Treasury</p>
        <h1 className="cb-h1">Cash Pool Management</h1>
        <hr className="cb-divider" />
      </div>

      {/* Drift detection banner — admin only */}
      {user.is_admin && diagnostic && Math.abs(diagnostic.drift_inr || 0) > 1 && (
        <div className="cb-notice items-start" style={{ marginBottom: 'var(--cb-space-6)' }} data-testid="cash-pool-drift-banner">
          <AlertTriangle className="w-5 h-5 cb-notice-icon mt-0.5" />
          <div className="flex-1">
            <p className="cb-notice-title">
              Cash Pool drift detected: ₹{Math.abs(diagnostic.drift_inr).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              {diagnostic.drift_inr > 0 ? ' more' : ' less'} than the ledger source-of-truth.
            </p>
            <p className="cb-notice-sub cb-num">
              Stored: ₹{(diagnostic.stored_inr || 0).toLocaleString('en-IN')} · Truth (from approved entries): ₹{(diagnostic.truth_inr || 0).toLocaleString('en-IN')}.
              This usually happens when an approve/edit was double-counted. Click "Recompute" to overwrite the stored balance with the truth value.
            </p>
          </div>
          <button
            onClick={handleRecompute}
            disabled={recomputing}
            className="cb-btn-primary flex-shrink-0"
            data-testid="cash-pool-recompute-btn"
          >
            <RefreshCw className={`w-4 h-4 ${recomputing ? 'animate-spin' : ''}`} />
            {recomputing ? 'Recomputing…' : 'Recompute'}
          </button>
        </div>
      )}

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="cb-card" data-testid="total-balance-card">
          <div className="flex items-center justify-between mb-2">
            <p className="cb-card-label">Total Cash Balance</p>
            <DollarSign className="w-6 h-6 cb-card-icon" />
          </div>
          <p className="cb-card-value cb-num">₹{balance?.balance?.toLocaleString() || '0'}</p>
        </div>

        <div className="cb-card" data-testid="petty-cash-card">
          <div className="flex items-center justify-between mb-2">
            <p className="cb-card-label">Petty Cash</p>
            <DollarSign className="w-6 h-6 cb-card-icon" />
          </div>
          <p className="cb-card-value cb-num">₹{balance?.petty_cash?.toLocaleString() || '0'}</p>
          <button
            onClick={() => setShowPettyCashForm(true)}
            className="cb-btn-secondary mt-2"
            style={{ fontSize: '0.75rem', padding: '4px 10px' }}
          >
            Update Petty Cash
          </button>
        </div>

        <div className="cb-card" data-testid="available-conversion-card">
          <div className="flex items-center justify-between mb-2">
            <p className="cb-card-label">Available for Conversion</p>
            <ArrowUpFromLine className="w-6 h-6 cb-card-icon" />
          </div>
          <p className="cb-card-value cb-num">₹{balance?.available_for_conversion?.toLocaleString() || '0'}</p>
          <button
            onClick={() => setShowConversionForm(true)}
            data-testid="convert-cash-button"
            className="cb-btn-secondary mt-2 w-full"
            style={{ justifyContent: 'center' }}
            disabled={balance?.available_for_conversion <= 0}
          >
            Send to Exchange (INR → USDT)
          </button>
        </div>

        <div className="cb-card" data-testid="fund-cash-pool-card">
          <div className="flex items-center justify-between mb-2">
            <p className="cb-card-label">Fund Cash Pool</p>
            <ArrowDownToLine className="w-6 h-6 cb-card-icon" />
          </div>
          <p className="cb-muted" style={{ fontSize: '0.875rem', marginBottom: 'var(--cb-space-2)' }}>Withdraw USDT to fund INR payouts</p>
          <button
            onClick={() => setShowUsdtToInrForm(true)}
            data-testid="fund-cash-pool-button"
            className="cb-btn-primary mt-2 w-full"
            style={{ justifyContent: 'center' }}
          >
            Withdraw USDT → INR
          </button>
        </div>
      </div>

      {/* Partner Cash Breakdown */}
      {partnerBalances && partnerBalances.length > 0 && (
        <div className="cb-card mb-6" data-testid="partner-breakdown-section">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center" style={{ gap: 'var(--cb-space-2)' }}>
              <Users className="w-5 h-5" style={{ color: 'var(--cb-ink-soft)' }} />
              <h2 className="cb-section-title">Cash Held by Each Partner</h2>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowSummary(true)}
                data-testid="open-summary-btn"
                className="cb-btn-secondary"
              >
                Summary Report
              </button>
              <button
                onClick={() => setShowTransferForm(true)}
                data-testid="open-partner-transfer-btn"
                className="cb-btn-primary"
              >
                <ArrowRightLeft className="w-4 h-4" />
                Transfer Between Partners
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {partnerBalances.map((p) => (
              <div
                key={p.partner_id}
                className="cb-card"
                style={{ padding: 'var(--cb-space-4)', boxShadow: 'none' }}
                data-testid={`partner-card-${p.partner_id}`}
              >
                <p className="cb-card-label">
                  {p.partner_id === 'historical' ? 'Pre-system (tag later)' : 'Partner'}
                </p>
                <p style={{ fontFamily: 'var(--cb-sans)', fontSize: '0.9375rem', color: 'var(--cb-ink)', fontWeight: 500, marginTop: 'var(--cb-space-1)' }}>{p.partner_name}</p>
                <p className={`cb-num ${p.balance < 0 ? 'cb-negative' : ''}`} style={{ fontFamily: 'var(--cb-serif)', fontSize: '1.375rem', marginTop: 'var(--cb-space-2)' }}>
                  ₹{(p.balance || 0).toLocaleString()}
                </p>
                <button
                  onClick={() => setDaybookPartner({ id: p.partner_id, name: p.partner_name })}
                  data-testid={`daybook-btn-${p.partner_id}`}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    color: 'var(--cb-ink)',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    marginTop: 'var(--cb-space-2)',
                    textDecoration: 'underline',
                    textDecorationColor: 'var(--cb-line)',
                  }}
                >
                  Day Book →
                </button>
              </div>
            ))}
          </div>
          <p className="cb-muted" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-3)' }}>
            Sum of all partner balances (excl. exchange) should approximately equal the Total Cash Balance above after full historical tagging.
          </p>
        </div>
      )}

      {/* Info Box */}
      <div className="cb-notice items-start mb-6" style={{ cursor: 'default' }}>
        <div className="flex-1">
          <p className="cb-notice-title">How Cash Pool Works</p>
          <ul className="cb-notice-sub" style={{ listStyle: 'none', padding: 0, marginTop: 'var(--cb-space-2)' }}>
            <li>• <strong>INR deposits</strong> from clients automatically add to Cash Pool</li>
            <li>• <strong>INR payouts</strong> to clients are paid from Cash Pool</li>
            <li>• When Cash Pool is low, <strong>withdraw USDT</strong> from exchange and convert to INR</li>
            <li>• Excess cash can be <strong>converted to USDT</strong> and sent to exchange</li>
          </ul>
        </div>
      </div>

      {/* Conversion History Tabs */}
      <div className="mb-8">
        <div className="cb-segment mb-4">
          <button
            onClick={() => setActiveTab('inrToUsdt')}
            className={`cb-btn-secondary ${activeTab === 'inrToUsdt' ? 'cb-btn-secondary--active' : ''}`}
            style={{ border: 'none' }}
          >
            <ArrowUpFromLine className="w-4 h-4" />
            Cash → USDT ({conversions.length})
          </button>
          <button
            onClick={() => setActiveTab('usdtToInr')}
            className={`cb-btn-secondary ${activeTab === 'usdtToInr' ? 'cb-btn-secondary--active' : ''}`}
            style={{ border: 'none' }}
          >
            <ArrowDownToLine className="w-4 h-4" />
            USDT → Cash ({usdtToInrConversions.length})
          </button>
        </div>

        {activeTab === 'inrToUsdt' && (
          <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
            <table data-testid="conversions-table" className="cb-table" style={{ minWidth: '100%' }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="cb-num">Amount (INR)</th>
                  <th className="cb-num">Rate</th>
                  <th className="cb-num">USDT Received</th>
                  <th className="cb-num">Fee</th>
                  <th>Destination</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {conversions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center cb-muted">No conversions yet</td>
                  </tr>
                ) : (
                  conversions.map((conv) => (
                    <tr key={conv.id}>
                      <td className="cb-num cb-muted" style={{ fontSize: '0.8125rem' }}>{new Date(conv.created_at).toLocaleDateString()}</td>
                      <td className="cb-num">₹{conv.amount_inr.toLocaleString()}</td>
                      <td className="cb-num cb-muted">{conv.conversion_rate}</td>
                      <td className="cb-num">{conv.usdt_amount.toLocaleString()} USDT</td>
                      <td className="cb-num cb-muted">{conv.fee} USDT</td>
                      <td style={{ fontWeight: 500 }}>{conv.destination_exchange}</td>
                      <td className="cb-muted" style={{ fontSize: '0.8125rem' }}>{conv.notes || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'usdtToInr' && (
          <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
            <table data-testid="usdt-to-inr-table" className="cb-table" style={{ minWidth: '100%' }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="cb-num">USDT Withdrawn</th>
                  <th className="cb-num">Rate</th>
                  <th className="cb-num">INR Received</th>
                  <th className="cb-num">Fee</th>
                  <th>Source Exchange</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {usdtToInrConversions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center cb-muted">No USDT withdrawals yet</td>
                  </tr>
                ) : (
                  usdtToInrConversions.map((conv) => (
                    <tr key={conv.id}>
                      <td className="cb-num cb-muted" style={{ fontSize: '0.8125rem' }}>{new Date(conv.created_at).toLocaleDateString()}</td>
                      <td className="cb-num">{conv.usdt_amount.toLocaleString()} USDT</td>
                      <td className="cb-num cb-muted">{conv.conversion_rate}</td>
                      <td className="cb-num">₹{conv.inr_received.toLocaleString()}</td>
                      <td className="cb-num cb-muted">{conv.fee_usdt} USDT</td>
                      <td style={{ fontWeight: 500 }}>{conv.source_exchange}</td>
                      <td className="cb-muted" style={{ fontSize: '0.8125rem' }}>{conv.notes || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Convert Cash to USDT Form */}
      {showConversionForm && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(11,11,10,0.55)' }}>
          <div className="cb-card w-full max-w-md" style={{ padding: 'var(--cb-space-6)' }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="cb-section-title">Convert Cash to USDT</h2>
              <button onClick={closeConversionForm} data-testid="close-conversion-form-button" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4" style={{ background: 'var(--cb-bone)', border: '1px solid var(--cb-line)', borderRadius: 'var(--cb-radius)', padding: 'var(--cb-space-3)' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--cb-ink)' }}>
                This sends INR from Cash Pool to an exchange as USDT.
              </p>
            </div>

            <form onSubmit={handleConversion} className="space-y-4">
              <div>
                <label className="form-label">Amount (INR) *</label>
                <input
                  type="number"
                  data-testid="conversion-amount-input"
                  step="0.01"
                  className="form-input"
                  value={conversionFormData.amount_inr}
                  onChange={(e) => setConversionFormData({ ...conversionFormData, amount_inr: e.target.value })}
                  required
                  max={balance?.available_for_conversion}
                />
                <p className="cb-muted cb-num" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-1)' }}>
                  Max available: ₹{balance?.available_for_conversion?.toLocaleString()}
                </p>
              </div>

              <div>
                <label className="form-label">Conversion Rate (INR per USDT) *</label>
                <input
                  type="number"
                  data-testid="conversion-rate-input"
                  step="0.01"
                  className="form-input"
                  value={conversionFormData.conversion_rate}
                  onChange={(e) => setConversionFormData({ ...conversionFormData, conversion_rate: e.target.value })}
                  required
                />
                <p className="cb-muted cb-num" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-1)' }}>
                  {conversionFormData.amount_inr && conversionFormData.conversion_rate && 
                    `= ${(parseFloat(conversionFormData.amount_inr) / parseFloat(conversionFormData.conversion_rate)).toFixed(2)} USDT`
                  }
                </p>
              </div>

              <div>
                <label className="form-label">Destination Exchange *</label>
                <input
                  type="text"
                  data-testid="destination-exchange-input"
                  className="form-input"
                  value={conversionFormData.destination_exchange}
                  onChange={(e) => setConversionFormData({ ...conversionFormData, destination_exchange: e.target.value })}
                  required
                  placeholder="e.g., Binance, Coinbase"
                />
              </div>

              <div>
                <label className="form-label">Drain From Partner *</label>
                <select
                  data-testid="conversion-partner-select"
                  className="form-input"
                  value={conversionFormData.partner_id}
                  onChange={(e) => setConversionFormData({ ...conversionFormData, partner_id: e.target.value })}
                  required
                >
                  <option value="">Select partner whose INR pool is drained…</option>
                  {partnerBalances.map((p) => (
                    <option key={p.partner_id} value={p.partner_id}>
                      {p.partner_name} (₹{Number(p.balance || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })})
                    </option>
                  ))}
                </select>
                <p className="cb-muted cb-num" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-1)' }}>The chosen partner's INR cash pool will be reduced by ₹{conversionFormData.amount_inr ? Number(conversionFormData.amount_inr).toLocaleString('en-IN') : '0'}.</p>
              </div>

              <div>
                <label className="form-label">Fee (USDT)</label>
                <input
                  type="number"
                  data-testid="conversion-fee-input"
                  step="0.01"
                  className="form-input"
                  value={conversionFormData.fee}
                  onChange={(e) => setConversionFormData({ ...conversionFormData, fee: e.target.value })}
                />
              </div>

              <div>
                <label className="form-label">Notes</label>
                <textarea
                  data-testid="conversion-notes-input"
                  className="form-input"
                  rows="2"
                  value={conversionFormData.notes}
                  onChange={(e) => setConversionFormData({ ...conversionFormData, notes: e.target.value })}
                />
              </div>

              <div className="flex gap-3">
                <button type="submit" data-testid="submit-conversion-form" className="cb-btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
                  Convert
                </button>
                <button type="button" onClick={closeConversionForm} className="cb-btn-secondary" style={{ flex: 1, justifyContent: 'center' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* USDT to INR Conversion Form */}
      {showUsdtToInrForm && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(11,11,10,0.55)' }}>
          <div className="cb-card w-full max-w-md" style={{ padding: 'var(--cb-space-6)' }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="cb-section-title">Withdraw USDT to Fund Cash Pool</h2>
              <button onClick={closeUsdtToInrForm} data-testid="close-usdt-to-inr-form" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4" style={{ background: 'var(--cb-bone)', border: '1px solid var(--cb-line)', borderRadius: 'var(--cb-radius)', padding: 'var(--cb-space-3)' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--cb-ink)' }}>
                <strong>Use this when:</strong> Cash Pool doesn't have enough INR to pay client profits. 
                Withdraw USDT from exchange, convert to INR, and add to Cash Pool.
              </p>
            </div>

            <form onSubmit={handleUsdtToInrConversion} className="space-y-4">
              <div>
                <label className="form-label">USDT Amount to Withdraw *</label>
                <input
                  type="number"
                  data-testid="usdt-amount-input"
                  step="0.01"
                  className="form-input"
                  value={usdtToInrFormData.usdt_amount}
                  onChange={(e) => setUsdtToInrFormData({ ...usdtToInrFormData, usdt_amount: e.target.value })}
                  required
                  placeholder="Amount in USDT"
                />
              </div>

              <div>
                <label className="form-label">Conversion Rate (INR per USDT) *</label>
                <input
                  type="number"
                  data-testid="usdt-to-inr-rate-input"
                  step="0.01"
                  className="form-input"
                  value={usdtToInrFormData.conversion_rate}
                  onChange={(e) => setUsdtToInrFormData({ ...usdtToInrFormData, conversion_rate: e.target.value })}
                  required
                />
                <p className="cb-num" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-1)', color: 'var(--cb-ink)', fontWeight: 500 }}>
                  {usdtToInrFormData.usdt_amount && usdtToInrFormData.conversion_rate && 
                    `INR to receive: ₹${((parseFloat(usdtToInrFormData.usdt_amount) - parseFloat(usdtToInrFormData.fee_usdt || 0)) * parseFloat(usdtToInrFormData.conversion_rate)).toLocaleString()}`
                  }
                </p>
              </div>

              <div>
                <label className="form-label">Source Exchange *</label>
                <input
                  type="text"
                  data-testid="source-exchange-input"
                  className="form-input"
                  value={usdtToInrFormData.source_exchange}
                  onChange={(e) => setUsdtToInrFormData({ ...usdtToInrFormData, source_exchange: e.target.value })}
                  required
                  placeholder="e.g., Binance, Coinbase"
                />
              </div>

              <div>
                <label className="form-label">Credit To Partner *</label>
                <select
                  data-testid="usdt-to-inr-partner-select"
                  className="form-input"
                  value={usdtToInrFormData.partner_id}
                  onChange={(e) => setUsdtToInrFormData({ ...usdtToInrFormData, partner_id: e.target.value })}
                  required
                >
                  <option value="">Select partner whose INR pool receives it…</option>
                  {partnerBalances.map((p) => (
                    <option key={p.partner_id} value={p.partner_id}>
                      {p.partner_name} (current ₹{Number(p.balance || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })})
                    </option>
                  ))}
                </select>
                <p className="cb-muted" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-1)' }}>The chosen partner's INR cash pool will be credited with the INR received.</p>
              </div>

              <div>
                <label className="form-label">Fee (USDT)</label>
                <input
                  type="number"
                  data-testid="usdt-to-inr-fee-input"
                  step="0.01"
                  className="form-input"
                  value={usdtToInrFormData.fee_usdt}
                  onChange={(e) => setUsdtToInrFormData({ ...usdtToInrFormData, fee_usdt: e.target.value })}
                  placeholder="Exchange/transfer fee"
                />
              </div>

              <div>
                <label className="form-label">Notes</label>
                <textarea
                  data-testid="usdt-to-inr-notes-input"
                  className="form-input"
                  rows="2"
                  value={usdtToInrFormData.notes}
                  onChange={(e) => setUsdtToInrFormData({ ...usdtToInrFormData, notes: e.target.value })}
                  placeholder="e.g., For January cycle payouts"
                />
              </div>

              <div className="flex gap-3">
                <button type="submit" data-testid="submit-usdt-to-inr-form" className="cb-btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
                  Fund Cash Pool
                </button>
                <button type="button" onClick={closeUsdtToInrForm} className="cb-btn-secondary" style={{ flex: 1, justifyContent: 'center' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Petty Cash Form */}
      {showPettyCashForm && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(11,11,10,0.55)' }}>
          <div className="cb-card w-full max-w-md" style={{ padding: 'var(--cb-space-6)' }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="cb-section-title">Update Petty Cash</h2>
              <button onClick={() => setShowPettyCashForm(false)} data-testid="close-petty-cash-form-button" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handlePettyCashUpdate} className="space-y-4">
              <div>
                <label className="form-label">Petty Cash Amount (INR) *</label>
                <input
                  type="number"
                  data-testid="petty-cash-input"
                  step="0.01"
                  className="form-input"
                  value={pettyCashAmount}
                  onChange={(e) => setPettyCashAmount(e.target.value)}
                  required
                  max={balance?.balance}
                />
                <p className="cb-muted cb-num" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-1)' }}>
                  Total balance: ₹{balance?.balance?.toLocaleString()}
                </p>
                <p className="cb-muted cb-num" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-1)' }}>
                  Available for conversion will be: ₹{((balance?.balance || 0) - (parseFloat(pettyCashAmount) || 0)).toLocaleString()}
                </p>
              </div>

              <div style={{ background: 'var(--cb-bone)', border: '1px solid var(--cb-line)', borderRadius: 'var(--cb-radius)', padding: 'var(--cb-space-3)' }}>
                <p style={{ fontSize: '0.875rem', color: 'var(--cb-ink)' }}>
                  <strong>Note:</strong> Petty cash is reserved for operations. Only the remaining balance can be converted to USDT.
                </p>
              </div>

              <div className="flex gap-3">
                <button type="submit" data-testid="submit-petty-cash-form" className="cb-btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
                  Update
                </button>
                <button type="button" onClick={() => setShowPettyCashForm(false)} className="cb-btn-secondary" style={{ flex: 1, justifyContent: 'center' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Partner Transfer Modal */}
      {showTransferForm && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(11,11,10,0.55)' }}>
          <div className="cb-card w-full max-w-lg" style={{ padding: 0 }}>
            <div className="flex justify-between items-center" style={{ padding: 'var(--cb-space-5)', borderBottom: '1px solid var(--cb-line)' }}>
              <h2 className="cb-section-title flex items-center" style={{ gap: 'var(--cb-space-2)' }}>
                <ArrowRightLeft className="w-5 h-5" />
                Transfer Cash Between Partners
              </h2>
              <button onClick={() => setShowTransferForm(false)} data-testid="close-partner-transfer-btn" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handlePartnerTransfer} className="space-y-4" style={{ padding: 'var(--cb-space-5)' }}>
              <PartnerDropdown
                value={transferData.source_partner_id}
                onChange={(id) => setTransferData({ ...transferData, source_partner_id: id })}
                required
                includeHistorical
                label="From partner"
                testId="partner-transfer-source"
              />
              <PartnerDropdown
                value={transferData.destination_partner_id}
                onChange={(id) => setTransferData({ ...transferData, destination_partner_id: id })}
                required
                label="To partner"
                testId="partner-transfer-dest"
              />
              <div>
                <label className="form-label">Amount (INR) *</label>
                <input
                  type="number" step="0.01"
                  value={transferData.amount}
                  onChange={(e) => setTransferData({ ...transferData, amount: e.target.value })}
                  data-testid="partner-transfer-amount"
                  className="form-input w-full text-sm"
                  required
                />
              </div>
              <div>
                <label className="form-label">Date *</label>
                <input
                  type="date"
                  value={transferData.date}
                  onChange={(e) => setTransferData({ ...transferData, date: e.target.value })}
                  data-testid="partner-transfer-date"
                  className="form-input w-full text-sm"
                  required
                />
              </div>
              <div>
                <label className="form-label">Notes</label>
                <textarea
                  rows={2}
                  value={transferData.notes}
                  onChange={(e) => setTransferData({ ...transferData, notes: e.target.value })}
                  data-testid="partner-transfer-notes"
                  className="form-input w-full text-sm"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowTransferForm(false)} className="cb-btn-secondary">Cancel</button>
                <button type="submit" className="cb-btn-primary" data-testid="partner-transfer-submit">Record Transfer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Day Book & Summary Modals */}
      {daybookPartner && (
        <PartnerDaybookModal
          partnerId={daybookPartner.id}
          partnerName={daybookPartner.name}
          onClose={() => setDaybookPartner(null)}
        />
      )}
      {showSummary && (
        <PartnerSummaryModal onClose={() => setShowSummary(false)} />
      )}
    </div>
  );
};

export default CashPool;
